import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Loan, LoanNote, AuditEntry, UpdateLoanRequest, GroupMember,
  LoanRequest, ClosedReason, LoanPhase, PackageFields,
  SemiAnnualReport, Invoice1544,
} from '@app/shared';
import {
  ROLE_ADMIN, ROLE_LOAN_ANALYST, ROLE_SPECIAL_ASSETS,
  PACKAGE_META, ALLOWED_PACKAGES_BY_PHASE, OPEN_REQUEST_STATUSES,
  PACKAGE_FIELD_KEYS,
  UPP_STATUSES, INVOICE_1544_REASONS, REPAIR_REASONS,
  PURCHASE_REASONS, capturesStatusAtCreate, UPP_WITHOUT_CO, carriesChargeOff, CHARGE_OFF_SHAPES,
  CLOSED_REASONS,
  ETRAN_LOAN_STATUSES, SBA_LOAN_STATUSES,
  OIC_DECISIONS, LIT_DECISIONS, RTS_DECISIONS, EXTENSION_REASONS,
  UPP_CO_NOT_REQUESTED_REASONS, SAR_STATUSES, ACTIONS,
  type PackageType,
} from '@app/shared';
import {
  ApiError,
  getLoan, updateLoan,
  listNotes, createNote, deleteNote,
  getLoanAudit,
  listLoanRequests, createRequest, updateRequest, deleteRequest,
  listSar, createSar, deleteSar,
  listInvoices1544, createInvoice1544, deleteInvoice1544,
} from '../api';
import { listMembersForRole } from '../graph';
import { useAuth } from '../auth/AuthProvider';
import {
  buildPackageFieldsFromRaw, humanizeKey, fmtFieldValue,
  NUMERIC_FIELD_KEYS, BOOLEAN_FIELD_KEYS, TEXT_FIELD_KEYS, isFieldVisible, litTotalFeesDisplay,
} from '../pkgFieldsUtil';
import { formatDate as fmt, addDays, addMonths } from '../dates';

// Formats an API failure for display, including the server-provided errorId when
// present so a user's report can be traced to the exact server log entry.
function describeError(e: unknown): string {
  if (e instanceof ApiError) return e.errorId ? `${e.message} (ref: ${e.errorId})` : e.message;
  return e instanceof Error ? e.message : String(e);
}

// Per-tab data loads that can fail independently of the main loan record.
type SectionKey = 'notes' | 'audit' | 'requests' | 'sars' | 'invoices';

type Tab =
  | 'overview'
  | 'repairDenial'
  | 'sar'
  | 'invoices1544'
  | 'requests'
  | 'notes'
  | 'audit';

const TAB_LABELS: Record<Tab, string> = {
  overview:     'Overview',
  requests:     'Packages',
  repairDenial: 'Repair / Denial',
  sar:          'SAR',
  invoices1544: '1544',
  notes:        'Notes',
  audit:        'Audit',
};

// Packages is the source of truth for all workflow data (UPP/OIC/Lit/CPC/
// Extension/Purchase/Charge-Off), so the old per-type read-only tabs are gone;
// their surviving loan-level fields now live in sections on Overview.
const TAB_ORDER: Tab[] = [
  'overview', 'requests', 'repairDenial', 'sar', 'invoices1544', 'notes', 'audit',
];

const PHASE_COLORS: Record<LoanPhase, string> = {
  'Prepurchase': 'status-active',
  'Liquidation': 'status-pending',
  'Charge Off':  'status-submitted',
  'Closed':      'status-final',
};

// The package types this user may initiate, given the loan's current phase.
function pkgTypesForUser(phase: LoanPhase, isAdmin: boolean, isSa: boolean, isAnalyst: boolean): PackageType[] {
  const allowed = ALLOWED_PACKAGES_BY_PHASE[phase];
  if (isAdmin) return allowed;
  return allowed.filter(t => {
    const initiator = PACKAGE_META[t].initiator;
    return (isSa && initiator === 'special_assets') || (isAnalyst && initiator === 'loan_analyst');
  });
}

// Default package type based on loan phase: Purchase for Prepurchase loans,
// otherwise no default so the user must pick.
function defaultPackageType(phase: LoanPhase): PackageType | '' {
  return phase === 'Prepurchase' ? 'Purchase' : '';
}

function fmtMoney(v: number | null) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}
// A field the package already captured at an earlier step. Shown for context so the
// analyst can see what was submitted, but not as an input — re-keying it was #12/#55
// ("enter the info once"). Status fields are deliberately NOT treated this way: eTran
// and Loan Status change as the loan moves, so they stay editable at every step.
function CapturedField({ label, value }: { label: string; value: string | null }) {
  return (
    <label className="detail-field">
      <span className="field-label">{label}</span>
      <input type="text" readOnly className="readonly" value={value || '—'} onChange={() => {}} />
      <span className="field-hint">Captured at Submit to SBA</span>
    </label>
  );
}

// What a finished package's status should read as. 'Decisioned' is accurate for a
// package SBA actually decided, but a UPP filed without a charge-off is simply done —
// nobody decided anything (#15/#17). Kept narrow on purpose: the fuller #15 treatment
// (Purchased, Charged Off, and so on) is still deferred.
function packageStatusLabel(r: { status: string; requestType: string | null; fields: PackageFields }): string {
  if (r.status !== 'Decisioned') return r.status;
  // A purchase that SBA granted was purchased — "Decisioned" says a decision happened
  // without saying what it was (#15).
  if (r.requestType === 'Purchase') return 'Purchased';
  if (r.requestType === 'UPP' && r.fields?.uppStatus === UPP_WITHOUT_CO) return 'Completed';
  return r.status;
}

// What the button that finishes a package should say. "Record Decision" is right where SBA
// hands down an answer; a purchase is completed, not decided (#15, and #25's spirit).
function decisionActionLabel(type: string | null): string {
  return type === 'Purchase' ? 'Complete Purchase' : 'Record Decision';
}

// A stored value that isn't in the list stays selectable. These fields were free text
// before they had approved values, so an existing entry has to read as itself rather
// than as a misleading blank over data that is still in the database.
const withCurrent = (options: readonly string[], value: string | null): readonly string[] =>
  value && !options.includes(value) ? [...options, value] : options;

// A package's typed workflow data, ready for display.
function packageFields(r: { fields: PackageFields }) {
  return Object.entries(r.fields ?? {}).filter(([, v]) => v !== '' && v != null && v !== false);
}

// Enum options for PackageFields keys that render as a <select> in the
// generic package field editor / read-only summary.
const FIELD_SELECT_OPTIONS: Partial<Record<keyof PackageFields, readonly string[]>> = {
  oicSbaDecision: OIC_DECISIONS,
  litSbaDecision: LIT_DECISIONS,
  rtsSbaDecision: RTS_DECISIONS,
  purchaseReason: PURCHASE_REASONS,
  chargeOffStatus: CHARGE_OFF_SHAPES,
  extensionReason: EXTENSION_REASONS,
  uppStatus: UPP_STATUSES,
  uppChargeOffNotRequestedReason: UPP_CO_NOT_REQUESTED_REASONS,
};

// Render a single PackageFields[key] input generically, editable or read-only.
function PackageFieldInput({ fieldKey, value, onChange, readOnly }: {
  fieldKey: keyof PackageFields;
  value: unknown;
  onChange: (v: unknown) => void;
  readOnly?: boolean;
}) {
  const label = humanizeKey(fieldKey as string);
  if (BOOLEAN_FIELD_KEYS.has(fieldKey)) {
    return <EditableCheckbox label={label} value={!!value} onChange={onChange} readOnly={readOnly} />;
  }
  const options = FIELD_SELECT_OPTIONS[fieldKey];
  if (options) {
    return <EditableSelect label={label} value={(value as string) ?? null} options={options} onChange={onChange} readOnly={readOnly} />;
  }
  if (NUMERIC_FIELD_KEYS.has(fieldKey)) {
    // Pass the raw string through while typing (so "100." / "100.0" aren't
    // collapsed mid-entry); it's coerced to a number on save.
    return (
      <EditableField label={label} value={value != null ? String(value) : null} type="number"
        onChange={onChange} readOnly={readOnly} />
    );
  }
  if (TEXT_FIELD_KEYS.has(fieldKey)) {
    return <EditableField label={label} value={(value as string) ?? null} onChange={onChange} readOnly={readOnly} />;
  }
  // Everything else is a date field.
  return <EditableField label={label} value={(value as string) ?? null} type="date" onChange={onChange} readOnly={readOnly} />;
}

// ---------------------------------------------------------------------------
// Field primitives
// ---------------------------------------------------------------------------

interface EditableFieldProps {
  label: string;
  value: string | null;
  type?: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}
function EditableField({ label, value, type = 'text', onChange, readOnly }: EditableFieldProps) {
  return (
    <label className="detail-field">
      <span className="field-label">{label}</span>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        readOnly={readOnly}
        className={readOnly ? 'readonly' : ''}
      />
    </label>
  );
}

interface EditableSelectProps {
  label: string;
  value: string | null;
  options: readonly string[];
  onChange: (v: string) => void;
  readOnly?: boolean;
}
function EditableSelect({ label, value, options, onChange, readOnly }: EditableSelectProps) {
  return (
    <label className="detail-field">
      <span className="field-label">{label}</span>
      <select value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={readOnly}>
        <option value="">— none —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

interface EditableCheckboxProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  readOnly?: boolean;
}
function EditableCheckbox({ label, value, onChange, readOnly }: EditableCheckboxProps) {
  return (
    <label className="detail-field detail-field-check">
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} disabled={readOnly} />
      <span className="field-label">{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LoanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const loanId = Number(id);
  const navigate = useNavigate();
  const { hasRole, me } = useAuth();
  const isAdmin = hasRole(ROLE_ADMIN);
  const isAnalyst = hasRole(ROLE_LOAN_ANALYST);
  const isSa = hasRole(ROLE_SPECIAL_ASSETS);
  // Every role that can open a loan can maintain it (#46). The fields that are
  // read-only are read-only for everybody, marked as such at the field.
  const canWrite = isAdmin || isAnalyst || isSa;
  const canNewPkg = isAdmin || isSa || isAnalyst;
  const canManagePkg = isAdmin || isAnalyst; // submit to SBA / record decision / cancel

  const [loan, setLoan] = useState<Loan | null>(null);
  const [notes, setNotes] = useState<LoanNote[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [requests, setRequests] = useState<LoanRequest[]>([]);
  const [sars, setSars] = useState<SemiAnnualReport[]>([]);
  const [invoices, setInvoices] = useState<Invoice1544[]>([]);
  const [analysts, setAnalysts] = useState<GroupMember[]>([]);
  const [saOfficers, setSaOfficers] = useState<GroupMember[]>([]);
  const [memberError, setMemberError] = useState<string | null>(null);
  // Per-section load failures, so a failed tab shows an error instead of looking empty.
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<SectionKey, string>>>({});

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  type PkgDetails = Record<string, string | boolean>;

  // "New Package" modal (create — replaces the old separate SA/analyst modals)
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [newRequestType, setNewRequestType] = useState<PackageType | ''>('');
  const [newRequestSaOfficerId, setNewRequestSaOfficerId] = useState('');
  const [newRequestAnalystId, setNewRequestAnalystId] = useState('');
  const [newRequestEtranStatus, setNewRequestEtranStatus] = useState('');
  const [newRequestSbaStatus, setNewRequestSbaStatus] = useState('');
  const [newRequestDescription, setNewRequestDescription] = useState('');
  const [newRequestDetails, setNewRequestDetails] = useState<PkgDetails>({});
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // "Record Decision" modal (advances a Pending package to Decisioned)
  const [decisionReq, setDecisionReq] = useState<LoanRequest | null>(null);
  const [decAnalystId, setDecAnalystId] = useState('');
  const [decEtranStatus, setDecEtranStatus] = useState('');
  const [decSbaStatus, setDecSbaStatus] = useState('');
  const [decComments, setDecComments] = useState('');
  const [decDetails, setDecDetails] = useState<PkgDetails>({});
  const [decClosedReason, setDecClosedReason] = useState<ClosedReason | ''>('');
  const [decSubmitting, setDecSubmitting] = useState(false);

  // "Submit to SBA" modal (advances a Requested package to Pending)
  const [submitReq, setSubmitReq] = useState<LoanRequest | null>(null);
  const [submitDetails, setSubmitDetails] = useState<PkgDetails>({});
  const [submitEtranStatus, setSubmitEtranStatus] = useState('');
  const [submitSbaStatus, setSubmitSbaStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // "Edit Package" modal (edits a package's fields directly, no status change)
  const [editPkgReq, setEditPkgReq] = useState<LoanRequest | null>(null);
  const [editPkgFields, setEditPkgFields] = useState<Record<string, unknown>>({});
  const [editPkgSaving, setEditPkgSaving] = useState(false);

  // SAR modal
  const [sarModalOpen, setSarModalOpen] = useState(false);
  const [newSarDate, setNewSarDate] = useState('');
  const [newSarStatus, setNewSarStatus] = useState('');
  const [newSarComments, setNewSarComments] = useState('');
  const [submittingSar, setSubmittingSar] = useState(false);

  // 1544 modal
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [newInvAmount, setNewInvAmount] = useState('');
  const [newInvDate, setNewInvDate] = useState('');
  const [newInvSubmitted, setNewInvSubmitted] = useState('');
  const [newInvReason, setNewInvReason] = useState('');
  const [newInvComments, setNewInvComments] = useState('');
  const [submittingInv, setSubmittingInv] = useState(false);

  const pendingEditsRef = useRef<UpdateLoanRequest>({});
  const autosaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadLoan = async () => {
    try { setLoan(await getLoan(loanId)); } catch (e) { setError(String(e)); }
  };
  // Each loader clears its own error on success and records it on failure, so a
  // transient/failed load surfaces in the tab rather than rendering as "empty".
  const clearSectionError = (key: SectionKey) => setSectionErrors(s => {
    if (!(key in s)) return s;
    const next = { ...s };
    delete next[key];
    return next;
  });
  const setSectionError = (key: SectionKey, e: unknown) => setSectionErrors(s => ({ ...s, [key]: describeError(e) }));

  const loadNotes    = async () => { try { setNotes(await listNotes(loanId)); clearSectionError('notes'); }    catch (e) { setSectionError('notes', e); } };
  const loadAudit    = async () => { try { setAudit(await getLoanAudit(loanId)); clearSectionError('audit'); } catch (e) { setSectionError('audit', e); } };
  const loadRequests = async () => { try { setRequests(await listLoanRequests(loanId)); clearSectionError('requests'); } catch (e) { setSectionError('requests', e); } };
  const loadSars     = async () => { try { setSars(await listSar(loanId)); clearSectionError('sars'); }       catch (e) { setSectionError('sars', e); } };
  const loadInvoices = async () => { try { setInvoices(await listInvoices1544(loanId)); clearSectionError('invoices'); } catch (e) { setSectionError('invoices', e); } };

  // Inline banner shown at the top of a data tab when its load failed, with a retry.
  const sectionErrorBanner = (key: SectionKey, label: string, retry: () => void | Promise<void>) =>
    sectionErrors[key] ? (
      <div className="error-banner">
        Couldn't load {label}: {sectionErrors[key]}{' '}
        <button className="btn-sm" onClick={() => void retry()}>Retry</button>
      </div>
    ) : null;

  useEffect(() => {
    void loadLoan();
    void loadNotes();
    void loadRequests();
    void loadSars();
    void loadInvoices();
    const onErr = (e: unknown) => { console.error('[members] failed to load group members', e); setMemberError(String(e)); };
    if (canWrite || canManagePkg) void listMembersForRole('loan_analyst').then(setAnalysts).catch(onErr);
    if (canWrite || canNewPkg) void listMembersForRole('special_assets').then(setSaOfficers).catch(onErr);
  }, [loanId]);

  useEffect(() => {
    if (activeTab === 'audit') void loadAudit();
  }, [activeTab]);

  const scheduleAutosave = (edits: UpdateLoanRequest) => {
    pendingEditsRef.current = { ...pendingEditsRef.current, ...edits };
    setSaveStatus('idle');
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(async () => {
      const pending = pendingEditsRef.current;
      if (Object.keys(pending).length === 0) return;
      pendingEditsRef.current = {};
      setSaveStatus('saving');
      try {
        await updateLoan(loanId, pending);
        setSaveStatus('saved');
        await loadLoan();
      } catch {
        setSaveStatus('error');
        pendingEditsRef.current = { ...pending, ...pendingEditsRef.current };
      }
    }, 800);
  };

  const field = (key: keyof UpdateLoanRequest, value: unknown) => {
    if (!canWrite) return;
    scheduleAutosave({ [key]: value === '' ? null : value } as UpdateLoanRequest);
    setLoan(l => l ? { ...l, [key]: value } : l);
  };

  const assign = (role: 'analyst' | 'sa', selectedId: string) => {
    // Analysts/admins assign either role; SA officers may only set the SA Rep.
    if (!canWrite) return;
    const pool = role === 'analyst' ? analysts : saOfficers;
    // Resolve the display name from the directory pool, else self, else the
    // current assignee — so "Myself" and the existing assignee work even when
    // the directory list is empty (e.g. an admin not in the analyst/SA group).
    let member = pool.find(m => m.id === selectedId) ?? null;
    if (!member && selectedId) {
      if (selectedId === me?.oid) member = { id: selectedId, displayName: me?.displayName ?? '', email: null };
      else if (role === 'analyst' && selectedId === loan?.assignedLoanAnalystId) member = { id: selectedId, displayName: loan?.assignedLoanAnalystName ?? '', email: null };
      else if (role === 'sa' && selectedId === loan?.assignedSaOfficerId) member = { id: selectedId, displayName: loan?.assignedSaOfficerName ?? '', email: null };
    }
    if (role === 'analyst') {
      const edits: UpdateLoanRequest = {
        assignedLoanAnalystId: member?.id ?? null,
        assignedLoanAnalystName: member?.displayName ?? null,
      };
      scheduleAutosave(edits);
      setLoan(l => l ? { ...l, ...edits } : l);
    } else {
      const edits: UpdateLoanRequest = {
        assignedSaOfficerId: member?.id ?? null,
        assignedSaOfficerName: member?.displayName ?? null,
      };
      scheduleAutosave(edits);
      setLoan(l => l ? { ...l, ...edits } : l);
    }
  };

  // Create a new package.
  const handleSubmitRequest = async () => {
    if (!newRequestType) return;
    setSubmittingRequest(true);
    try {
      const initiator = PACKAGE_META[newRequestType].initiator;
      // Only keys valid for this package type (per PACKAGE_FIELD_KEYS) are
      // sent — create-form-only scratch fields (uccSearch, siteVisit, reason,
      // offeror, amountOffered, etc.) are not real PackageFields and are dropped.
      const fields = buildPackageFieldsFromRaw(newRequestType, newRequestDetails);
      const saChosen = saOfficers.find(m => m.id === newRequestSaOfficerId);
      const analystChosen = analysts.find(m => m.id === newRequestAnalystId);
      await createRequest({
        loanId,
        requestType: newRequestType,
        description: newRequestDescription.trim() || undefined,
        ...(initiator === 'special_assets'
          ? { assignSaOfficerId: saChosen?.id ?? me?.oid, assignSaOfficerName: saChosen?.displayName ?? me?.displayName }
          : { assignAnalystId: analystChosen?.id ?? me?.oid, assignAnalystName: analystChosen?.displayName ?? me?.displayName }),
        sbaLoanStatus: newRequestSbaStatus || undefined,
        etranLoanStatus: newRequestEtranStatus || undefined,
        fields,
      });
      setRequestModalOpen(false);
      setNewRequestType('');
      setNewRequestDescription('');
      setNewRequestSaOfficerId('');
      setNewRequestAnalystId('');
      setNewRequestEtranStatus('');
      setNewRequestSbaStatus('');
      setNewRequestDetails({});
      await loadLoan();
      await loadRequests();
      setActiveTab('requests');
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmittingRequest(false);
    }
  };

  // Advance a Requested package to Pending (loan analyst has sent it to SBA).
  const handleSubmitToSba = async () => {
    if (!submitReq) return;
    setSubmitting(true);
    try {
      const fields = buildPackageFieldsFromRaw(submitReq.requestType as PackageType, submitDetails);
      await updateRequest(submitReq.id, {
        status: 'Pending',
        fields,
        sbaLoanStatus: submitSbaStatus || undefined,
        etranLoanStatus: submitEtranStatus || undefined,
      });
      setSubmitReq(null);
      setSubmitDetails({});
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
    finally { setSubmitting(false); }
  };

  // Withdraw an open (Requested or Pending) package.
  const handleCancelRequest = async (r: LoanRequest) => {
    try {
      await updateRequest(r.id, { status: 'Cancelled' });
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
  };

  // Park a package so another can be worked, and bring it back (#52). Resuming names the
  // package that will be parked in its place, because that is a change to a second record
  // and the officer should not discover it afterwards.
  const handleHoldRequest = async (r: LoanRequest) => {
    if (!confirm(`Put this ${r.requestType} package on hold? The loan's phase goes back to what it was before the package was opened, and the package can be resumed later.`)) return;
    try {
      await updateRequest(r.id, { status: 'On Hold' });
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
  };

  const handleResumeRequest = async (r: LoanRequest) => {
    const open = requests.find(x => x.id !== r.id && (OPEN_REQUEST_STATUSES as string[]).includes(x.status));
    const warning = open
      ? `Resume this ${r.requestType} package? The open ${open.requestType} package will be put on hold.`
      : `Resume this ${r.requestType} package?`;
    if (!confirm(warning)) return;
    try {
      // The API resumes it to whatever it was parked at; 'Pending' here just means "resume".
      await updateRequest(r.id, { status: 'Pending' });
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
  };

  // Edit a package's fields directly (no status change).
  const openEditPkgModal = (r: LoanRequest) => {
    setEditPkgReq(r);
    setEditPkgFields({ ...r.fields });
  };
  const handleSaveEditPkg = async () => {
    if (!editPkgReq) return;
    setEditPkgSaving(true);
    try {
      await updateRequest(editPkgReq.id, { fields: buildPackageFieldsFromRaw(editPkgReq.requestType as PackageType, editPkgFields) });
      setEditPkgReq(null);
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
    finally { setEditPkgSaving(false); }
  };

  // Delete a package outright (admin-only).
  const handleDeletePkg = async (r: LoanRequest) => {
    if (!confirm('Delete this package? This cannot be undone.')) return;
    try {
      await deleteRequest(r.id);
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
  };

  // Record SBA's decision on a Pending package; both move the loan's phase
  // depending on package type, so reload the loan too.
  const handleRecordDecision = async () => {
    if (!decisionReq) return;
    setDecSubmitting(true);
    try {
      const chosen = analysts.find(m => m.id === decAnalystId);
      const isPurchase = decisionReq.requestType === 'Purchase';
      // A UPP filed with a charge-off resolves the loan exactly as a Charge Off package
      // does (#17), so the milestone dates and the closed reason apply to it too.
      const isChargeOff = carriesChargeOff(decisionReq.requestType as PackageType, decisionReq.fields);
      const fields = buildPackageFieldsFromRaw(decisionReq.requestType as PackageType, decDetails);
      // Loan-level milestone dates a decision may set — written to the loan,
      // not the package (purchaseDate for Purchase; final/uncollectible dates
      // for Charge Off).
      const loanMilestones: { purchaseDate?: string | null; principalBalanceAtPurchase?: number | null; dateFinalSbaChargeOff?: string | null; dateSbaUncollectible?: string | null } = {};
      if (isPurchase && decDetails.purchaseDate) loanMilestones.purchaseDate = String(decDetails.purchaseDate);
      // The balance goes to the loan, not the package — it is one number per loan (#31).
      if (isPurchase && decDetails.principalBalanceAtPurchase !== undefined && decDetails.principalBalanceAtPurchase !== '') {
        loanMilestones.principalBalanceAtPurchase = Number(decDetails.principalBalanceAtPurchase);
      }
      if (isChargeOff && decDetails.dateFinalSbaChargeOff) loanMilestones.dateFinalSbaChargeOff = String(decDetails.dateFinalSbaChargeOff);
      if (isChargeOff && decDetails.dateSbaUncollectible) loanMilestones.dateSbaUncollectible = String(decDetails.dateSbaUncollectible);
      await updateRequest(decisionReq.id, {
        status: 'Decisioned',
        fields,
        ...(Object.keys(loanMilestones).length > 0 ? { loanMilestones } : {}),
        ...(isChargeOff ? { closedReason: decClosedReason as ClosedReason } : {}),
        sbaLoanStatus: decSbaStatus || undefined,
        etranLoanStatus: decEtranStatus || undefined,
        assignAnalystId: chosen?.id ?? me?.oid,
        assignAnalystName: chosen?.displayName ?? me?.displayName,
        description: decComments.trim() || undefined,
      });
      setDecisionReq(null);
      await loadLoan();
      await loadRequests();
    } catch (e) { setError(String(e)); }
    finally { setDecSubmitting(false); }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await createNote(loanId, { noteText: newNote.trim() });
      setNewNote('');
      await loadNotes();
    } catch (e) { setError(String(e)); }
    finally { setAddingNote(false); }
  };
  const handleDeleteNote = async (noteId: number) => {
    if (!confirm('Delete this note?')) return;
    try { await deleteNote(loanId, noteId); await loadNotes(); }
    catch (e) { setError(String(e)); }
  };

  // SAR handlers
  const handleSubmitSar = async () => {
    if (!newSarDate) return;
    setSubmittingSar(true);
    try {
      await createSar(loanId, {
        reportDate: newSarDate,
        currentStatus: newSarStatus || undefined,
        comments: newSarComments.trim() || undefined,
      });
      setSarModalOpen(false);
      setNewSarDate(''); setNewSarStatus(''); setNewSarComments('');
      await loadSars();
    } catch (e) { setError(String(e)); }
    finally { setSubmittingSar(false); }
  };
  const handleDeleteSar = async (id: number) => {
    if (!confirm('Delete this SAR entry?')) return;
    try { await deleteSar(id); await loadSars(); } catch (e) { setError(String(e)); }
  };

  // 1544 handlers
  const handleSubmitInv = async () => {
    setSubmittingInv(true);
    try {
      await createInvoice1544(loanId, {
        invoiceAmount: newInvAmount ? parseFloat(newInvAmount) : undefined,
        invoiceDate: newInvDate || undefined,
        dateSubmittedForPayment: newInvSubmitted || undefined,
        reason: newInvReason || undefined,
        comments: newInvComments.trim() || undefined,
      });
      setInvModalOpen(false);
      setNewInvAmount(''); setNewInvDate(''); setNewInvSubmitted(''); setNewInvReason(''); setNewInvComments('');
      await loadInvoices();
    } catch (e) { setError(String(e)); }
    finally { setSubmittingInv(false); }
  };
  const handleDeleteInv = async (id: number) => {
    if (!confirm('Delete this 1544 invoice?')) return;
    try { await deleteInvoice1544(id); await loadInvoices(); } catch (e) { setError(String(e)); }
  };

  if (!loan) return <div className="page"><p className="loading">Loading loan…</p></div>;

  // Computed read-only dates derived from purchaseDate.
  const uppDueDate = addDays(loan.purchaseDate, 45);
  const extensionDueDate = addMonths(loan.purchaseDate, 23);

  // A loan has at most one open (Requested/Pending) package at a time.
  const hasOpenPackage = requests.some(r => (OPEN_REQUEST_STATUSES as string[]).includes(r.status));
  const canOpenPackage = loan.phase !== 'Closed' && !hasOpenPackage && !loan.isDeleted;
  const pkgTypes = pkgTypesForUser(loan.phase, isAdmin, isSa, isAnalyst);

  const openPkgModal = () => {
    const types = pkgTypesForUser(loan.phase, isAdmin, isSa, isAnalyst);
    const def = defaultPackageType(loan.phase);
    setNewRequestType(types.includes(def as PackageType) ? def : '');
    setNewRequestSaOfficerId(me?.oid ?? '');
    setNewRequestAnalystId(me?.oid ?? '');
    setNewRequestEtranStatus(loan.etranLoanStatus ?? '');
    setNewRequestSbaStatus(loan.sbaLoanStatus ?? '');
    setNewRequestDescription('');
    setNewRequestDetails({});
    setRequestModalOpen(true);
  };

  const openDecisionModal = (r: LoanRequest) => {
    setDecisionReq(r);
    setDecAnalystId(me?.oid ?? '');
    setDecEtranStatus(loan.etranLoanStatus ?? '');
    // Note the RTS status pair is NOT seeded here: it follows the Approved/Declined answer
    // instead (see applyRtsDecision). Seeding it on open would have pre-filled "Returned to
    // Regular Servicing" on a package SBA went on to decline.
    setDecSbaStatus(loan.sbaLoanStatus ?? '');
    setDecComments('');
    // Fields the analyst may revise at decision but shouldn't have to retype are seeded
    // from the request. (Fields captured at Submit render read-only straight from
    // r.fields instead — see CapturedField — so they are deliberately not seeded.)
    setDecDetails(r.fields?.extensionReason ? { extensionReason: String(r.fields.extensionReason) } : {});
    setDecClosedReason('');
  };

  const openSubmitModal = (r: LoanRequest) => {
    setSubmitReq(r);
    setSubmitDetails({});
    // Prefill with the loan's current external statuses so the analyst updates
    // rather than re-keys them (#19 — status is set/updated at the Submit step).
    setSubmitEtranStatus(loan?.etranLoanStatus ?? '');
    setSubmitSbaStatus(loan?.sbaLoanStatus ?? '');
  };

  const setDetail = (key: string, val: string | boolean) =>
    setNewRequestDetails(d => ({ ...d, [key]: val }));

  const setDecDetail = (key: string, val: string | boolean) =>
    setDecDetails(d => ({ ...d, [key]: val }));

  // Recording SBA's answer on a Return to Regular Servicing package also settles the loan's
  // two statuses, which is the rest of the Status Legend's spec for it: "Update Loan status to
  // (Returned to Regular Serviciing), Update Etran Status (Disbursed Current)". Only on
  // Approved -- a declined modification leaves the loan where it was, and Line Status falls
  // back to Ongoing Liquidation (or Open Bankruptcy) on its own. Both stay editable.
  const applyRtsDecision = (decision: string) => {
    setDecDetail('rtsSbaDecision', decision);
    if (decision === 'Approved') {
      setDecSbaStatus('Returned to Regular Servicing');
      setDecEtranStatus('Disbursed Current');
    } else {
      setDecSbaStatus(loan.sbaLoanStatus ?? '');
      setDecEtranStatus(loan.etranLoanStatus ?? '');
    }
  };
  const setSubmitDetail = (key: string, val: string | boolean) =>
    setSubmitDetails(d => ({ ...d, [key]: val }));

  // Tab labels with counts where useful.
  const tabLabel = (t: Tab) => {
    if (t === 'requests') return `${TAB_LABELS[t]} (${requests.length})`;
    if (t === 'notes')    return `${TAB_LABELS[t]} (${notes.length})`;
    if (t === 'sar')      return `${TAB_LABELS[t]} (${sars.length})`;
    if (t === 'invoices1544') return `${TAB_LABELS[t]} (${invoices.length})`;
    return TAB_LABELS[t];
  };

  return (
    <div className="page loan-detail">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/loans')}>← Back</button>
        <div className="page-title">
          <h1>{loan.borrowerName}</h1>
          <span className="loan-meta">SBA #{loan.sbaLoanNumber}{loan.umwsbLoanNumber ? ` · UMWSB #${loan.umwsbLoanNumber}` : ''}</span>
        </div>
        <div className="save-status">
          {saveStatus === 'saving' && <span className="saving">Saving…</span>}
          {saveStatus === 'saved' && <span className="saved">Saved</span>}
          {saveStatus === 'error' && <span className="save-error">Save failed</span>}
        </div>
        {canNewPkg && !loan.isDeleted && (
          <button className="btn-primary" onClick={openPkgModal} disabled={!canOpenPackage}>+ New Package</button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loan.isDeleted && <div className="deleted-banner">This record has been soft-deleted.</div>}

      <div className="status-bar">
        <label className="detail-field">
          <span className="field-label">Phase</span>
          <span>
            <span className={`status-badge ${PHASE_COLORS[loan.phase]}`}>{loan.phase}</span>
          </span>
        </label>
        {/* Phase is system-driven (package workflow) and no longer hand-editable (#14).
            Line Status is the derived, granular status users read. */}
        {loan.lineStatus && (
          <label className="detail-field">
            <span className="field-label">Line Status</span>
            <span><span className="status-badge status-pending">{loan.lineStatus}</span></span>
          </label>
        )}
        <label className="detail-field">
          <span className="field-label">Assigned Analyst</span>
          <select value={loan.assignedLoanAnalystId ?? ''} onChange={e => assign('analyst', e.target.value)} disabled={!canWrite}>
            <option value="">— unassigned —</option>
            {/* Always allow self-assignment, even when not in the directory list. */}
            {me?.oid && !analysts.some(m => m.id === me.oid) && (
              <option value={me.oid}>Myself{me.displayName ? ` (${me.displayName})` : ''}</option>
            )}
            {/* Keep the current assignee visible even if the directory list didn't load. */}
            {loan.assignedLoanAnalystId && loan.assignedLoanAnalystId !== me?.oid && !analysts.some(m => m.id === loan.assignedLoanAnalystId) && (
              <option value={loan.assignedLoanAnalystId}>{loan.assignedLoanAnalystName ?? 'Current assignee'}</option>
            )}
            {analysts.map(m => <option key={m.id} value={m.id}>{m.displayName}</option>)}
          </select>
        </label>
        <label className="detail-field">
          <span className="field-label">Special Assets Rep</span>
          <select value={loan.assignedSaOfficerId ?? ''} onChange={e => assign('sa', e.target.value)} disabled={!canWrite}>
            <option value="">— unassigned —</option>
            {me?.oid && !saOfficers.some(m => m.id === me.oid) && (
              <option value={me.oid}>Myself{me.displayName ? ` (${me.displayName})` : ''}</option>
            )}
            {loan.assignedSaOfficerId && loan.assignedSaOfficerId !== me?.oid && !saOfficers.some(m => m.id === loan.assignedSaOfficerId) && (
              <option value={loan.assignedSaOfficerId}>{loan.assignedSaOfficerName ?? 'Current assignee'}</option>
            )}
            {saOfficers.map(m => <option key={m.id} value={m.id}>{m.displayName}</option>)}
          </select>
        </label>
      </div>
      {memberError && (
        <p className="member-hint">
          Couldn't load the user directory for the assignment dropdowns ({memberError}). Current assignments are still shown; new picks are unavailable until this resolves.
        </p>
      )}

      <div className="tabs">
        {TAB_ORDER.map(t => (
          <button
            key={t}
            className={`tab${activeTab === t ? ' active' : ''}`}
            onClick={() => setActiveTab(t)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      {/* ===================================================================
          Overview tab — Main Page + extract-sourced loan info + flags + meta
          =================================================================== */}
      {activeTab === 'overview' && (
        <div className="tab-content detail-grid">
          <section>
            <h3>Overview</h3>
            <EditableField label="Borrower Name" value={loan.borrowerName} onChange={() => {}} readOnly />
            <EditableField label="UMWSB Loan #" value={loan.umwsbLoanNumber} onChange={() => {}} readOnly />
            <EditableField label="SBA Loan #" value={loan.sbaLoanNumber} readOnly onChange={() => {}} />
            {isAdmin && (
              <EditableField label="Loan Type (2=Loan, 4=Liquidation)" value={loan.loanType} onChange={v => field('loanType', v || null)} readOnly={!canWrite} />
            )}
            <EditableCheckbox label="Early Default" value={loan.earlyDefault} onChange={v => field('earlyDefault', v)} readOnly={!canWrite} />
            {/* Editable because nothing else can set it. The monthly extract carries the
                original loan amount and the participated balance but no current one, while
                the client's own report layout lists Legal Balance — so until it is added to
                the extract, a person typing it is the only writer there is. Read-only, it
                was a permanently empty column being exported to the client as data. */}
            <EditableField label="Loan Balance" value={loan.legalBalance?.toString() ?? null} type="number"
              onChange={v => field('legalBalance', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            <EditableField label="Guaranty Balance" value={loan.guarantyBalance?.toString() ?? null} type="number" onChange={v => field('guarantyBalance', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            <EditableCheckbox label="Sold Loan" value={loan.soldLoan} onChange={v => field('soldLoan', v)} readOnly={!canWrite} />
            {/* The two external statuses, kept together and in the order every package
                form presents them. Settable here as well as through the package flow:
                they change for reasons that have nothing to do with a package being in
                flight, and the values are the approved list either way (#44). */}
            <EditableSelect label="eTran Loan Status" value={loan.etranLoanStatus}
              options={withCurrent(ETRAN_LOAN_STATUSES, loan.etranLoanStatus)}
              onChange={v => field('etranLoanStatus', v || null)} readOnly={!canWrite} />
            <EditableSelect label="Loan Status" value={loan.sbaLoanStatus}
              options={withCurrent(SBA_LOAN_STATUSES, loan.sbaLoanStatus)}
              onChange={v => field('sbaLoanStatus', v || null)} readOnly={!canWrite} />
          </section>

          <section>
            <h3>Tracking Flags</h3>
            <EditableCheckbox label="Active BK or Litigation" value={loan.activeBkOrLitigation} onChange={v => field('activeBkOrLitigation', v)} readOnly={!canWrite} />
            <EditableCheckbox label="Collateral in Liquidation" value={loan.collateralInLiquidation} onChange={v => field('collateralInLiquidation', v)} readOnly={!canWrite} />
            <EditableCheckbox label="Approved Written Plan" value={loan.approvedWrittenPlan} onChange={v => field('approvedWrittenPlan', v)} readOnly={!canWrite} />
          </section>

          <section>
            <h3>Action &amp; Site Visits</h3>
            <EditableField label="Next Action Date" value={loan.nextActionDate} type="date" onChange={v => field('nextActionDate', v || null)} readOnly={!canWrite} />
            <EditableSelect label="Action" value={loan.action} options={withCurrent(ACTIONS, loan.action)}
              onChange={v => field('action', v || null)} readOnly={!canWrite} />
            <EditableField label="Date Initial Site Visit" value={loan.dateInitialSiteVisit} type="date" onChange={v => field('dateInitialSiteVisit', v || null)} readOnly={!canWrite} />
          </section>

          <section>
            <h3>Loan Information</h3>
            <EditableField label="State" value={loan.stateLocated} onChange={() => {}} readOnly />
            <EditableField label="NAICS Code" value={loan.naicsCode} onChange={v => field('naicsCode', v)} readOnly={!canWrite} />
            {/* Looked up from the code, never stored (migration 030) — so it follows an
                edit to NAICS Code rather than going stale beside it. */}
            <EditableField label="NAICS Description" value={loan.naicsDescription} onChange={() => {}} readOnly />
            <EditableField label="Approval Gross" value={loan.approvalGross?.toString() ?? null} type="number" onChange={v => field('approvalGross', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            <EditableField label="Guaranty %" value={loan.guarantyPct?.toString() ?? null} type="number" onChange={() => {}} readOnly />
            <EditableField label="Note Date" value={loan.noteDate} type="date" onChange={() => {}} readOnly />
            <EditableField label="Date First Disbursed" value={loan.dateFirstDisbursed} type="date" onChange={v => field('dateFirstDisbursed', v || null)} readOnly={!canWrite} />
            <EditableField label="Maturity Date" value={loan.maturityDate} type="date" onChange={() => {}} readOnly />
            <EditableField label="Interest Rate" value={loan.interestRate?.toString() ?? null} type="number" onChange={v => field('interestRate', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            <EditableCheckbox label="In Business" value={loan.inBusiness ?? false} onChange={v => field('inBusiness', v)} readOnly={!canWrite} />
            <EditableField label="Next Due Date" value={loan.nextDueDate} type="date" onChange={v => field('nextDueDate', v || null)} readOnly={!canWrite} />
            <EditableField label="Date in Liquidation" value={loan.dateInLiquidation} type="date" onChange={() => {}} readOnly />
            <EditableField label="Date of Default" value={loan.dateOfDefault} type="date" onChange={v => field('dateOfDefault', v || null)} readOnly={!canWrite} />
          </section>

          {/* Loan-level milestone/condition fields — the workflow itself lives on
              the Packages tab; these are the loan record's own dates/amounts. */}
          <section>
            <h3>Purchase</h3>
            <EditableField label="Purchase Date" value={loan.purchaseDate} type="date" onChange={v => field('purchaseDate', v || null)} readOnly={!canWrite} />
            <EditableField label="Principal Balance at Purchase" value={loan.principalBalanceAtPurchase?.toString() ?? null} type="number"
              onChange={v => field('principalBalanceAtPurchase', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            {/* Mirrored from whichever package recorded them, so read-only here — the
                correction path is that package's Edit form, which mirrors again. A loan
                shows the latest, which is what the report layouts ask for. */}
            <EditableField label="Date to SBA Liquidation" value={loan.dateToSbaLiquidation} type="date" readOnly onChange={() => {}} />
            <EditableField label="Date Demand Letter Sent" value={loan.dateDemandLetterSent} type="date" readOnly onChange={() => {}} />
            <EditableField label="Date Charge Off Submitted" value={loan.dateChargeOffSubmitted} type="date" readOnly onChange={() => {}} />
            <EditableField label="UMWSB Charged Off Date" value={loan.chargedOffDate} type="date" readOnly onChange={() => {}} />
            <EditableField label="Date Extension Requested" value={loan.dateExtensionRequested} type="date" readOnly onChange={() => {}} />
            <EditableField label="Date Extension Approved" value={loan.dateExtensionApproval} type="date" readOnly onChange={() => {}} />
            <EditableField label="Date Extension Expires" value={loan.dateExtensionExpires} type="date" readOnly onChange={() => {}} />
            <EditableField label="Accrued Expenses" value={loan.accruedExpenses?.toString() ?? null} type="number" onChange={v => field('accruedExpenses', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            <EditableField label="UPP Due Date (purchase + 45 days)" value={uppDueDate} readOnly onChange={() => {}} />
            <EditableField label="Extension Due Date (purchase + 23 months)" value={extensionDueDate} readOnly onChange={() => {}} />
          </section>


          <section>
            <h3>Charge Off</h3>
            {/* Set when the package was decisioned, and correctable here afterwards: the
                only alternative was re-deciding a closed package. It drives the loan's
                terminal Line Status, so a wrong pick is visible everywhere until fixed. */}
            {loan.phase === 'Closed' && (
              <EditableSelect label="Closed Reason" value={loan.closedReason} options={CLOSED_REASONS}
                onChange={v => field('closedReason', (v || null) as ClosedReason | null)} readOnly={!canWrite} />
            )}
            <EditableField label="Date of SBA Charge Off" value={loan.dateFinalSbaChargeOff} type="date" onChange={v => field('dateFinalSbaChargeOff', v || null)} readOnly={!canWrite} />
            <EditableField label="Date of SBA Uncollectible" value={loan.dateSbaUncollectible} type="date" onChange={v => field('dateSbaUncollectible', v || null)} readOnly={!canWrite} />
          </section>

          <section className="meta-section">
            <h3>Record Info</h3>
            <div className="meta-row"><span>Created</span><span>{fmt(loan.createdAt)}{loan.createdByName ? ` by ${loan.createdByName}` : ''}</span></div>
            <div className="meta-row"><span>Last Updated</span><span>{loan.updatedAt ? `${fmt(loan.updatedAt)}${loan.updatedByName ? ` by ${loan.updatedByName}` : ''}` : '—'}</span></div>
            {loan.isDeleted && <div className="meta-row deleted-meta"><span>Deleted</span><span>{fmt(loan.deletedAt)}{loan.deletedByName ? ` by ${loan.deletedByName}` : ''}</span></div>}
          </section>
        </div>
      )}

      {/* ===================================================================
          SAR tab
          =================================================================== */}
      {/* Repair / Denial is rare enough that it crowded the Overview (#58). Same
          fields, same autosave — just somewhere you go looking for them. */}
      {activeTab === 'repairDenial' && (
        <div className="tab-content detail-grid">
          <section>
            <h3>Repair / Denial</h3>
            <EditableCheckbox label="Repair / Denial Pending" value={loan.repairDenialPending} onChange={v => field('repairDenialPending', v)} readOnly={!canWrite} />
            <EditableField label="Amount of Repair / Denial" value={loan.repairAmount?.toString() ?? null} type="number" onChange={v => field('repairAmount', v ? parseFloat(v) : null)} readOnly={!canWrite} />
            <EditableSelect label="Reason" value={loan.repairReason} options={REPAIR_REASONS} onChange={v => field('repairReason', v || null)} readOnly={!canWrite} />
            <EditableField label="Date Invoice Received" value={loan.repairInvoiceReceivedDate} type="date" onChange={v => field('repairInvoiceReceivedDate', v || null)} readOnly={!canWrite} />
            <EditableField label="Date Invoice Paid" value={loan.repairInvoicePaidDate} type="date" onChange={v => field('repairInvoicePaidDate', v || null)} readOnly={!canWrite} />
          </section>
        </div>
      )}

      {activeTab === 'sar' && (
        <div className="tab-content sar-tab">
          {sectionErrorBanner('sars', 'the semi-annual reports', loadSars)}
          {canWrite && (
            <div className="tab-toolbar">
              <button className="btn-primary" onClick={() => {
                // A report states the loan's condition, so it opens on that rather than
                // blank — "SAR status doesn't match the loan status" (#56).
                setNewSarStatus(loan.sbaLoanStatus ?? '');
                setSarModalOpen(true);
              }}>+ Add SAR</button>
            </div>
          )}
          {sars.length === 0 ? (
            <p className="empty">No SAR entries yet.</p>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Report Date</th>
                  <th>Current Status</th>
                  <th>Comments</th>
                  <th>Created</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {sars.map(s => (
                  <tr key={s.id}>
                    <td>{fmt(s.reportDate)}</td>
                    <td>{s.currentStatus ?? '—'}</td>
                    <td className="audit-value">{s.comments ?? '—'}</td>
                    <td>{fmt(s.createdAt)}{s.createdByName ? ` · ${s.createdByName}` : ''}</td>
                    {isAdmin && (
                      <td><button className="btn-sm btn-danger" onClick={() => handleDeleteSar(s.id)}>×</button></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ===================================================================
          1544 tab
          =================================================================== */}
      {activeTab === 'invoices1544' && (
        <div className="tab-content invoices-tab">
          {sectionErrorBanner('invoices', 'the 1544 invoices', loadInvoices)}
          {canWrite && (
            <div className="tab-toolbar">
              <button className="btn-primary" onClick={() => setInvModalOpen(true)}>+ Add 1544 Invoice</button>
            </div>
          )}
          {invoices.length === 0 ? (
            <p className="empty">No 1544 invoices yet.</p>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Invoice Date</th>
                  <th>Amount</th>
                  <th>Submitted for Payment</th>
                  <th>Reason</th>
                  <th>Comments</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i.id}>
                    <td>{fmt(i.invoiceDate)}</td>
                    <td className="money">{fmtMoney(i.invoiceAmount)}</td>
                    <td>{fmt(i.dateSubmittedForPayment)}</td>
                    <td>{i.reason ?? '—'}</td>
                    <td className="audit-value">{i.comments ?? '—'}</td>
                    {isAdmin && (
                      <td><button className="btn-sm btn-danger" onClick={() => handleDeleteInv(i.id)}>×</button></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ===================================================================
          Requests / Packages tab
          =================================================================== */}
      {activeTab === 'requests' && (
        <div className="tab-content requests-tab">
          {sectionErrorBanner('requests', 'the packages', loadRequests)}
          {!loan.isDeleted && canNewPkg && (
            <div className="tab-toolbar">
              <button className="btn-primary" onClick={openPkgModal} disabled={!canOpenPackage}>
                + New Package
              </button>
              {hasOpenPackage && <span className="muted">A package is open — advance it, put it on hold, or cancel it before starting another.</span>}
              {loan.phase === 'Closed' && <span className="muted">This loan is closed.</span>}
            </div>
          )}
          {requests.length === 0 ? (
            <p className="empty">No packages submitted on this loan.</p>
          ) : (
            <div className="requests-list">
              {requests.map(r => (
                <div key={r.id} className={`request-card status-${r.status.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="request-header">
                    <span className="status-badge">{packageStatusLabel(r)}</span>
                    {r.requestType && <span className="request-type">{r.requestType}</span>}
                    <span className="request-submitter">
                      {r.submittedByName ?? 'Unknown'} · {new Date(r.submittedAt).toLocaleString()}
                    </span>
                  </div>
                  {r.description && <p className="request-description">{r.description}</p>}
                  {packageFields(r).length > 0 && (
                    <dl className="request-fields">
                      {packageFields(r).map(([k, v]) => (
                        <div key={k} className="request-field">
                          <dt>{humanizeKey(k)}</dt>
                          <dd>{fmtFieldValue(v) ?? ''}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <div className="request-actions">
                    {canManagePkg && (r.status === 'Requested' || r.status === 'Pending') && (
                      <>
                        {r.status === 'Requested' && (
                          <button className="btn-sm btn-primary" onClick={() => openSubmitModal(r)}>
                            Submit to SBA
                          </button>
                        )}
                        {r.status === 'Pending' && (
                          <button className="btn-sm btn-success" onClick={() => openDecisionModal(r)}>
                            {decisionActionLabel(r.requestType)}
                          </button>
                        )}
                        <button className="btn-sm" onClick={() => handleHoldRequest(r)}>
                          Put on hold
                        </button>
                        <button className="btn-sm btn-danger" onClick={() => handleCancelRequest(r)}>
                          Cancel
                        </button>
                      </>
                    )}
                    {canManagePkg && r.status === 'On Hold' && (
                      <>
                        <button className="btn-sm btn-primary" onClick={() => handleResumeRequest(r)}>
                          Resume
                        </button>
                        <button className="btn-sm btn-danger" onClick={() => handleCancelRequest(r)}>
                          Cancel
                        </button>
                      </>
                    )}
                    {canManagePkg && r.requestType && (
                      <button className="btn-sm" onClick={() => openEditPkgModal(r)}>
                        Edit
                      </button>
                    )}
                    {isAdmin && (
                      <button className="btn-sm btn-danger" onClick={() => handleDeletePkg(r)}>
                        Delete
                      </button>
                    )}
                  </div>
                  {r.closedAt && (
                    <p className="request-closed-meta">
                      {packageStatusLabel(r)} {fmt(r.closedAt)} by {r.closedByName ?? 'Unknown'}
                      {r.closedNote ? ` — ${r.closedNote}` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===================================================================
          Notes tab
          =================================================================== */}
      {activeTab === 'notes' && (
        <div className="tab-content notes-tab">
          {sectionErrorBanner('notes', 'the notes', loadNotes)}
          {canWrite && (
            <div className="add-note">
              <textarea
                rows={3}
                placeholder="Add a note…"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
              />
              <button className="btn-primary" onClick={handleAddNote} disabled={addingNote || !newNote.trim()}>
                {addingNote ? 'Adding…' : 'Add Note'}
              </button>
            </div>
          )}
          {notes.length === 0 ? (
            <p className="empty">No notes yet.</p>
          ) : (
            <div className="notes-list">
              {notes.map(n => (
                <div key={n.id} className="note-card">
                  <div className="note-header">
                    <span className="note-author">{n.createdByName ?? 'Unknown'}</span>
                    <span className="note-date">{fmt(n.createdAt)}</span>
                    {isAdmin && (
                      <button className="btn-sm btn-danger note-delete" onClick={() => handleDeleteNote(n.id)}>×</button>
                    )}
                  </div>
                  <p className="note-text">{n.noteText}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===================================================================
          Audit tab
          =================================================================== */}
      {activeTab === 'audit' && (
        <div className="tab-content audit-tab">
          {sectionErrorBanner('audit', 'the audit log', loadAudit)}
          {audit.length === 0 ? (
            <p className="empty">No audit entries.</p>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Field</th>
                  <th>From</th>
                  <th>To</th>
                </tr>
              </thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.id}>
                    <td>{new Date(a.changedAt).toLocaleString()}</td>
                    <td>{a.changedByName ?? '—'}</td>
                    <td><span className={`action-badge action-${a.action.toLowerCase()}`}>{a.action}</span></td>
                    <td className="mono">{a.fieldName ?? '—'}</td>
                    {/* title so a value too long for the column is still readable on hover —
                        a package's opening values and the import's derivation note both run
                        past it, and an ellipsis with no way to see the rest is a dead end. */}
                    <td className="audit-value" title={a.previousValue ?? undefined}>{a.previousValue ?? '—'}</td>
                    <td className="audit-value" title={a.newValue ?? undefined}>{a.newValue ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ===================================================================
          "New Package" modal (create)
          =================================================================== */}
      {requestModalOpen && (
        <div className="modal-backdrop">
          <div className="modal modal-wide">
            <div className="modal-header">
              <h2>New Package</h2>
              <p className="modal-meta">{loan.borrowerName} · SBA #{loan.sbaLoanNumber}</p>
            </div>
            <div className="modal-body">
            {pkgTypes.length === 0 ? (
              <p className="empty">No package types are available to start for this loan in its current phase.</p>
            ) : (
              <>
                <label className="detail-field">
                  <span className="field-label">Package Type *</span>
                  <select value={newRequestType} onChange={e => { setNewRequestType(e.target.value as PackageType | ''); setNewRequestDetails({}); }}>
                    <option value="">— select —</option>
                    {pkgTypes.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>

                {newRequestType && (
                  <>
                    {newRequestType === 'UPP' && (
                      <>
                        <div className="modal-section-label">UPP Details</div>
                        <label className="detail-field">
                          <span className="field-label">Date UPP Submitted</span>
                          <input type="date" value={String(newRequestDetails.uppSubmittedDate ?? '')}
                            onChange={e => setDetail('uppSubmittedDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Status</span>
                          <select value={String(newRequestDetails.uppStatus ?? '')} onChange={e => setDetail('uppStatus', e.target.value)}>
                            <option value="">— select —</option>
                            {UPP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                        {newRequestDetails.uppStatus === UPP_WITHOUT_CO && (
                          <label className="detail-field">
                            <span className="field-label">Reason Charge Off Not Requested</span>
                            <select value={String(newRequestDetails.uppChargeOffNotRequestedReason ?? '')}
                              onChange={e => setDetail('uppChargeOffNotRequestedReason', e.target.value)}>
                              <option value="">— select —</option>
                              {UPP_CO_NOT_REQUESTED_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </label>
                        )}
                        {/* With a charge-off, the UPP *is* the charge-off submission (#17), so it
                            carries the same fields — and the CPC claim too when the shape says so. */}
                        {carriesChargeOff('UPP', newRequestDetails) && (
                          <>
                            <div className="modal-section-label">Charge Off</div>
                            <label className="detail-field">
                              <span className="field-label">Date Charge Off Submitted</span>
                              <input type="date" value={String(newRequestDetails.dateChargeOffSubmitted ?? '')}
                                onChange={e => setDetail('dateChargeOffSubmitted', e.target.value)} />
                            </label>
                            <label className="detail-field">
                              <span className="field-label">Expenses Submitted</span>
                              <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.chargeOffExpensesSubmitted ?? '')}
                                onChange={e => setDetail('chargeOffExpensesSubmitted', e.target.value)} />
                            </label>
                          </>
                        )}
                        {String(newRequestDetails.uppStatus ?? '').includes('CPC') && (
                          <>
                            <div className="modal-section-label">CPC</div>
                            <label className="detail-field">
                              <span className="field-label">Date CPC Submitted</span>
                              <input type="date" value={String(newRequestDetails.cpcSubmittedDate ?? '')}
                                onChange={e => setDetail('cpcSubmittedDate', e.target.value)} />
                            </label>
                            <label className="detail-field">
                              <span className="field-label">Amount of CPC Submitted</span>
                              <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.cpcSubmittedAmount ?? '')}
                                onChange={e => setDetail('cpcSubmittedAmount', e.target.value)} />
                            </label>
                          </>
                        )}
                      </>
                    )}


                    {newRequestType === 'Purchase' && (
                      <>
                        <div className="modal-section-label">Purchase Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">UCC Search Date</span>
                          <input type="date" value={String(newRequestDetails.uccSearchDate ?? '')}
                            onChange={e => setDetail('uccSearchDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Site Visit Date</span>
                          <input type="date" value={String(newRequestDetails.siteVisitDate ?? '')}
                            onChange={e => setDetail('siteVisitDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Reason</span>
                          <select value={String(newRequestDetails.purchaseReason ?? '')}
                            onChange={e => setDetail('purchaseReason', e.target.value)}>
                            <option value="">— select —</option>
                            {PURCHASE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Date Demand Letter Sent</span>
                          <input type="date" value={String(newRequestDetails.dateDemandLetterSent ?? '')}
                            onChange={e => setDetail('dateDemandLetterSent', e.target.value)} />
                        </label>
                      </>
                    )}

                    {newRequestType === 'Charge Off' && (
                      <>
                        <div className="modal-section-label">Charge Off Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Collateral Liquidated</span>
                          <select value={newRequestDetails.collateralLiquidated === true ? 'yes' : newRequestDetails.collateralLiquidated === false ? 'no' : ''}
                            onChange={e => setDetail('collateralLiquidated', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Expenses Incurred</span>
                          <select value={newRequestDetails.expensesIncurred === true ? 'yes' : newRequestDetails.expensesIncurred === false ? 'no' : ''}
                            onChange={e => setDetail('expensesIncurred', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Expenses Amount</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.expensesAmount ?? '')}
                            onChange={e => setDetail('expensesAmount', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Refer to Treasury</span>
                          <select value={newRequestDetails.referToTreasury === true ? 'yes' : newRequestDetails.referToTreasury === false ? 'no' : ''}
                            onChange={e => setDetail('referToTreasury', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                      </>
                    )}


                    {newRequestType === 'OIC' && (
                      <>
                        <div className="modal-section-label">OIC Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Offeror</span>
                          <input type="text" value={String(newRequestDetails.oicOfferor ?? '')}
                            onChange={e => setDetail('oicOfferor', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Amount Offered</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.oicAmountOffered ?? '')}
                            onChange={e => setDetail('oicAmountOffered', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">UMWSB Approval Date</span>
                          <input type="date" value={String(newRequestDetails.oicUmwsbApprovalDate ?? '')}
                            onChange={e => setDetail('oicUmwsbApprovalDate', e.target.value)} />
                        </label>
                      </>
                    )}

                    {newRequestType === 'Lit Plan' && (
                      <>
                        <div className="modal-section-label">Litigation Plan Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Fees &amp; Costs to Date — Approved by SBA</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.litFeesApprovedToDate ?? '')}
                            onChange={e => setDetail('litFeesApprovedToDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Fees &amp; Costs to Date — Not Approved by SBA</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.litFeesNotApprovedToDate ?? '')}
                            onChange={e => setDetail('litFeesNotApprovedToDate', e.target.value)} />
                        </label>
                        {/* Derived, not entered: the two figures above are exhaustive. */}
                        <label className="detail-field">
                          <span className="field-label">Total Fees &amp; Costs to Date</span>
                          <input type="text" readOnly className="readonly" onChange={() => {}}
                            value={litTotalFeesDisplay(newRequestDetails)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Total Budget Requested</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(newRequestDetails.litTotalBudgetRequested ?? '')}
                            onChange={e => setDetail('litTotalBudgetRequested', e.target.value)} />
                        </label>
                      </>
                    )}

                    {newRequestType === 'Extension' && (
                      <>
                        <div className="modal-section-label">Extension Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Length of Extension</span>
                          <input type="text" placeholder="e.g. 6 months" value={String(newRequestDetails.extensionLength ?? '')}
                            onChange={e => setDetail('extensionLength', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Expected Resolution</span>
                          <input type="date" value={String(newRequestDetails.extensionExpectedResolution ?? '')}
                            onChange={e => setDetail('extensionExpectedResolution', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Reason</span>
                          <select value={String(newRequestDetails.extensionReason ?? '')}
                            onChange={e => setDetail('extensionReason', e.target.value)}>
                            <option value="">— select —</option>
                            {EXTENSION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </label>
                      </>
                    )}

                    {newRequestType === 'Return to Regular Servicing' && (
                      <>
                        <div className="modal-section-label">Return to Regular Servicing (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Modification Completed</span>
                          <select value={newRequestDetails.rtsModificationCompleted === true ? 'yes' : newRequestDetails.rtsModificationCompleted === false ? 'no' : ''}
                            onChange={e => setDetail('rtsModificationCompleted', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                      </>
                    )}

                    {/* ── Shared fields ── */}
                    <div className="modal-section-label">Shared Information</div>

                    {PACKAGE_META[newRequestType].initiator === 'special_assets' ? (
                      <label className="detail-field">
                        <span className="field-label">Special Assets Officer</span>
                        <select value={newRequestSaOfficerId} onChange={e => setNewRequestSaOfficerId(e.target.value)}>
                          <option value={me?.oid ?? ''}>Myself{me?.displayName ? ` (${me.displayName})` : ''}</option>
                          {saOfficers.filter(m => m.id !== me?.oid).map(m => (
                            <option key={m.id} value={m.id}>{m.displayName}</option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label className="detail-field">
                        <span className="field-label">Loan Analyst</span>
                        <select value={newRequestAnalystId} onChange={e => setNewRequestAnalystId(e.target.value)}>
                          <option value={me?.oid ?? ''}>Myself{me?.displayName ? ` (${me.displayName})` : ''}</option>
                          {analysts.filter(m => m.id !== me?.oid).map(m => (
                            <option key={m.id} value={m.id}>{m.displayName}</option>
                          ))}
                        </select>
                      </label>
                    )}

                    {/* Analyst-initiated packages (UPP/CPC) set both statuses at create.
                        Purchase does too, but from the other side: Special Assets records the
                        loan's status when it submits the request (#48) — the analyst can still
                        revise both at Submit to SBA. Every other SA-initiated type leaves them
                        to the analyst's Submit/Decision step (#5). */}
                    {capturesStatusAtCreate(newRequestType) && (
                      <>
                        <label className="detail-field">
                          <span className="field-label">eTran Loan Status</span>
                          <select value={newRequestEtranStatus} onChange={e => setNewRequestEtranStatus(e.target.value)}>
                            <option value="">— none —</option>
                            {ETRAN_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Loan Status</span>
                          <select value={newRequestSbaStatus} onChange={e => setNewRequestSbaStatus(e.target.value)}>
                            <option value="">— none —</option>
                            {SBA_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                      </>
                    )}

                    <label className="detail-field">
                      <span className="field-label">Comments</span>
                      <textarea rows={3} value={newRequestDescription} onChange={e => setNewRequestDescription(e.target.value)} />
                    </label>
                  </>
                )}
              </>
            )}
            </div>

            <div className="modal-actions">
              <button className="btn-sm" onClick={() => setRequestModalOpen(false)} disabled={submittingRequest}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmitRequest} disabled={submittingRequest || !newRequestType}>
                {submittingRequest ? 'Submitting…' : 'Submit Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sarModalOpen && (
        <div className="modal-backdrop">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Semi-Annual Report</h2>
              <p className="modal-meta">{loan.borrowerName} · SBA #{loan.sbaLoanNumber}</p>
            </div>
            <div className="modal-body">
            <label className="detail-field">
              <span className="field-label">Report Date *</span>
              <input type="date" value={newSarDate} onChange={e => setNewSarDate(e.target.value)} />
            </label>
            <label className="detail-field">
              <span className="field-label">Current Status</span>
              <select value={newSarStatus} onChange={e => setNewSarStatus(e.target.value)}>
                <option value="">— none —</option>
                {SAR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="detail-field">
              <span className="field-label">Comments</span>
              <textarea rows={4} value={newSarComments} onChange={e => setNewSarComments(e.target.value)} />
            </label>
            </div>
            <div className="modal-actions">
              <button className="btn-sm" onClick={() => setSarModalOpen(false)} disabled={submittingSar}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmitSar} disabled={submittingSar || !newSarDate}>
                {submittingSar ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {invModalOpen && (
        <div className="modal-backdrop">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add 1544 Invoice</h2>
              <p className="modal-meta">{loan.borrowerName} · SBA #{loan.sbaLoanNumber}</p>
            </div>
            <div className="modal-body">
            <label className="detail-field">
              <span className="field-label">Amount of Invoice</span>
              <input type="number" value={newInvAmount} onChange={e => setNewInvAmount(e.target.value)} />
            </label>
            <label className="detail-field">
              <span className="field-label">Date of Invoice</span>
              <input type="date" value={newInvDate} onChange={e => setNewInvDate(e.target.value)} />
            </label>
            <label className="detail-field">
              <span className="field-label">Date Submitted for Payment</span>
              <input type="date" value={newInvSubmitted} onChange={e => setNewInvSubmitted(e.target.value)} />
            </label>
            <label className="detail-field">
              <span className="field-label">Reason for Invoice</span>
              <select value={newInvReason} onChange={e => setNewInvReason(e.target.value)}>
                <option value="">— none —</option>
                {INVOICE_1544_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="detail-field">
              <span className="field-label">Comments</span>
              <textarea rows={3} value={newInvComments} onChange={e => setNewInvComments(e.target.value)} />
            </label>
            </div>
            <div className="modal-actions">
              <button className="btn-sm" onClick={() => setInvModalOpen(false)} disabled={submittingInv}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmitInv} disabled={submittingInv}>
                {submittingInv ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          "Record Decision" modal (advances a Pending package to Decisioned)
          =================================================================== */}
      {decisionReq && (
        <div className="modal-backdrop">
          <div className="modal modal-wide">
            <div className="modal-header">
              <h2>{decisionActionLabel(decisionReq.requestType)}</h2>
              <p className="modal-meta">{loan.borrowerName} · <strong>{decisionReq.requestType}</strong></p>
            </div>
            <div className="modal-body">
            {decisionReq.requestType === 'Purchase' && (
              <>
                <div className="modal-section-label">Purchase Details</div>
                {/* Date in Liquidation Status & Date Purchase Submitted are captured at
                    the Submit step (#12) — not re-asked here. */}
                <label className="detail-field"><span className="field-label">Purchase Date</span><input type="date" value={String(decDetails.purchaseDate ?? '')} onChange={e => setDecDetail('purchaseDate', e.target.value)} /></label>
                {/* A purchase-time fact, like the two either side of it, and one the report
                    layouts expect ("Purchase Date | Principal Balance at Purchase | Time
                    Since Purchased"). Typed by the analyst rather than copied from the
                    loan's Loan Balance: the two may legitimately differ, and #31 asks who
                    owns the number, not just where it goes. */}
                <label className="detail-field"><span className="field-label">Principal Balance at Purchase</span><input type="number" step="0.01" placeholder="0.00" value={String(decDetails.principalBalanceAtPurchase ?? '')} onChange={e => setDecDetail('principalBalanceAtPurchase', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">Disallowed Interest</span><input type="number" step="0.01" placeholder="0.00" value={String(decDetails.disallowedInterest ?? '')} onChange={e => setDecDetail('disallowedInterest', e.target.value)} /></label>
                {decDetails.purchaseDate && (
                  <>
                    <label className="detail-field"><span className="field-label">UPP Due Date (purchase + 45 days)</span><input type="text" readOnly className="readonly" value={addDays(String(decDetails.purchaseDate), 45) ?? ''} onChange={() => {}} /></label>
                    <label className="detail-field"><span className="field-label">Extension Due Date (purchase + 23 months)</span><input type="text" readOnly className="readonly" value={addMonths(String(decDetails.purchaseDate), 23) ?? ''} onChange={() => {}} /></label>
                  </>
                )}
              </>
            )}
            {decisionReq.requestType === 'OIC' && (
              <>
                <div className="modal-section-label">OIC Details</div>
                {decisionReq.fields?.oicSubmittedDate ? (
                  <CapturedField label="Date OIC Submitted" value={fmt(decisionReq.fields.oicSubmittedDate)} />
                ) : (
                  <label className="detail-field"><span className="field-label">Date OIC Submitted</span><input type="date" value={String(decDetails.oicSubmittedDate ?? '')} onChange={e => setDecDetail('oicSubmittedDate', e.target.value)} /></label>
                )}
                <label className="detail-field"><span className="field-label">Date Decision Received by SBA</span><input type="date" value={String(decDetails.oicDecisionReceivedDate ?? '')} onChange={e => setDecDetail('oicDecisionReceivedDate', e.target.value)} /></label>
                <label className="detail-field">
                  <span className="field-label">SBA Decision</span>
                  <select value={String(decDetails.oicSbaDecision ?? '')} onChange={e => setDecDetail('oicSbaDecision', e.target.value)}>
                    <option value="">— select —</option>
                    {OIC_DECISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
                <label className="detail-field"><span className="field-label">Amount Approved</span><input type="number" step="0.01" placeholder="0.00" value={String(decDetails.oicAmountApproved ?? '')} onChange={e => setDecDetail('oicAmountApproved', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">Due Date</span><input type="date" value={String(decDetails.oicDueDate ?? '')} onChange={e => setDecDetail('oicDueDate', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">Date Paid</span><input type="date" value={String(decDetails.oicDatePaid ?? '')} onChange={e => setDecDetail('oicDatePaid', e.target.value)} /></label>
              </>
            )}
            {decisionReq.requestType === 'Lit Plan' && (
              <>
                <div className="modal-section-label">Litigation Plan Details</div>
                {decisionReq.fields?.litPlanSubmittedDate ? (
                  <CapturedField label="Date Lit Plan Submitted" value={fmt(decisionReq.fields.litPlanSubmittedDate)} />
                ) : (
                  <label className="detail-field"><span className="field-label">Date Lit Plan Submitted</span><input type="date" value={String(decDetails.litPlanSubmittedDate ?? '')} onChange={e => setDecDetail('litPlanSubmittedDate', e.target.value)} /></label>
                )}
                <label className="detail-field"><span className="field-label">Amount of Legal Expenses</span><input type="number" step="0.01" placeholder="0.00" value={String(decDetails.litLegalExpenses ?? '')} onChange={e => setDecDetail('litLegalExpenses', e.target.value)} /></label>
                <label className="detail-field">
                  <span className="field-label">SBA Decision</span>
                  <select value={String(decDetails.litSbaDecision ?? '')} onChange={e => setDecDetail('litSbaDecision', e.target.value)}>
                    <option value="">— select —</option>
                    {LIT_DECISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
                <label className="detail-field"><span className="field-label">Amended Plan Submitted</span><input type="date" value={String(decDetails.litAmendedPlanSubmittedDate ?? '')} onChange={e => setDecDetail('litAmendedPlanSubmittedDate', e.target.value)} /></label>
              </>
            )}
            {decisionReq.requestType === 'Extension' && (
              <>
                <div className="modal-section-label">Extension Details</div>
                {decisionReq.fields?.dateExtensionRequested ? (
                  <CapturedField label="Date Extension Submitted" value={fmt(decisionReq.fields.dateExtensionRequested)} />
                ) : (
                  <label className="detail-field"><span className="field-label">Date Extension Submitted</span><input type="date" value={String(decDetails.dateExtensionRequested ?? '')} onChange={e => setDecDetail('dateExtensionRequested', e.target.value)} /></label>
                )}
                <label className="detail-field"><span className="field-label">Date Extension Approved</span><input type="date" value={String(decDetails.dateExtensionApproval ?? '')} onChange={e => setDecDetail('dateExtensionApproval', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">Date Extension Expires</span><input type="date" value={String(decDetails.dateExtensionExpires ?? '')} onChange={e => setDecDetail('dateExtensionExpires', e.target.value)} /></label>
                {/* Special Assets picks the reason on the request (#43), but it can change by
                    the time SBA decides — so it defaults to their choice and stays editable,
                    like the status fields. */}
                <label className="detail-field">
                  <span className="field-label">Reason for Extension</span>
                  <select value={String(decDetails.extensionReason ?? '')} onChange={e => setDecDetail('extensionReason', e.target.value)}>
                    <option value="">— select —</option>
                    {EXTENSION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
              </>
            )}
            {decisionReq.requestType === 'Return to Regular Servicing' && (
              <>
                <div className="modal-section-label">Return to Regular Servicing Details</div>
                {decisionReq.fields?.rtsSubmittedDate ? (
                  <CapturedField label="Date Submitted" value={fmt(decisionReq.fields.rtsSubmittedDate)} />
                ) : (
                  <label className="detail-field"><span className="field-label">Date Submitted</span><input type="date" value={String(decDetails.rtsSubmittedDate ?? '')} onChange={e => setDecDetail('rtsSubmittedDate', e.target.value)} /></label>
                )}
                <label className="detail-field">
                  <span className="field-label">SBA Decision</span>
                  <select value={String(decDetails.rtsSbaDecision ?? '')} onChange={e => applyRtsDecision(e.target.value)}>
                    <option value="">— select —</option>
                    {RTS_DECISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
              </>
            )}
            {carriesChargeOff(decisionReq.requestType as PackageType, decisionReq.fields) && (
              <>
                <div className="modal-section-label">Charge Off Details</div>
                {decisionReq.fields?.dateChargeOffSubmitted ? (
                  <CapturedField label="Date CO Submitted" value={fmt(decisionReq.fields.dateChargeOffSubmitted)} />
                ) : (
                  <label className="detail-field"><span className="field-label">Date CO Submitted</span><input type="date" value={String(decDetails.dateChargeOffSubmitted ?? '')} onChange={e => setDecDetail('dateChargeOffSubmitted', e.target.value)} /></label>
                )}
                <label className="detail-field"><span className="field-label">Date of SBA Charge Off</span><input type="date" value={String(decDetails.dateFinalSbaChargeOff ?? '')} onChange={e => setDecDetail('dateFinalSbaChargeOff', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">Date of SBA Uncollectible</span><input type="date" value={String(decDetails.dateSbaUncollectible ?? '')} onChange={e => setDecDetail('dateSbaUncollectible', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">UMWSB Charged Off Date</span><input type="date" value={String(decDetails.chargedOffDate ?? '')} onChange={e => setDecDetail('chargedOffDate', e.target.value)} /></label>
                <label className="detail-field">
                  <span className="field-label">Closed Reason *</span>
                  <select value={decClosedReason} onChange={e => setDecClosedReason(e.target.value as ClosedReason | '')}>
                    <option value="">— select —</option>
                    <option value="SBA Charged Off">SBA Charged Off</option>
                    <option value="SBA Uncollectible">SBA Uncollectible</option>
                  </select>
                </label>
              </>
            )}

            <div className="modal-section-label">Shared Information</div>
            <label className="detail-field">
              <span className="field-label">Loan Analyst</span>
              <select value={decAnalystId} onChange={e => setDecAnalystId(e.target.value)}>
                <option value={me?.oid ?? ''}>Myself{me?.displayName ? ` (${me.displayName})` : ''}</option>
                {analysts.filter(m => m.id !== me?.oid).map(m => <option key={m.id} value={m.id}>{m.displayName}</option>)}
              </select>
            </label>
            <label className="detail-field">
              <span className="field-label">eTran Loan Status</span>
              <select value={decEtranStatus} onChange={e => setDecEtranStatus(e.target.value)}>
                <option value="">— none —</option>
                {ETRAN_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="detail-field">
              <span className="field-label">Loan Status</span>
              <select value={decSbaStatus} onChange={e => setDecSbaStatus(e.target.value)}>
                <option value="">— none —</option>
                {SBA_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {/* The claim was recorded at submit; what SBA actually paid is known now (#38).
                Shown for whichever package carried it — a Charge Off, or a UPP filed
                "with CO and CPC". */}
            {isFieldVisible('cpcReceivedDate', (decisionReq.fields ?? {}) as Record<string, unknown>) && (
              <>
                <div className="modal-section-label">CPC</div>
                <label className="detail-field">
                  <span className="field-label">Date CPC Received</span>
                  <input type="date" value={String(decDetails.cpcReceivedDate ?? '')}
                    onChange={e => setDecDetail('cpcReceivedDate', e.target.value)} />
                </label>
                <label className="detail-field">
                  <span className="field-label">Amount of CPC Received</span>
                  <input type="number" step="0.01" placeholder="0.00" value={String(decDetails.cpcReceivedAmount ?? '')}
                    onChange={e => setDecDetail('cpcReceivedAmount', e.target.value)} />
                </label>
              </>
            )}

            <label className="detail-field">
              <span className="field-label">Comments</span>
              <textarea rows={3} value={decComments} onChange={e => setDecComments(e.target.value)} />
            </label>
            </div>

            <div className="modal-actions">
              <button className="btn-sm" onClick={() => setDecisionReq(null)} disabled={decSubmitting}>Cancel</button>
              <button className="btn-success" onClick={handleRecordDecision}
                disabled={decSubmitting || (carriesChargeOff(decisionReq.requestType as PackageType, decisionReq.fields) && !decClosedReason)}>
                {decSubmitting ? 'Saving…' : decisionActionLabel(decisionReq.requestType)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          "Submit to SBA" modal (advances a Requested package to Pending)
          =================================================================== */}
      {submitReq && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h2>Submit to SBA</h2>
              <p className="modal-meta">{loan.borrowerName} · <strong>{submitReq.requestType}</strong></p>
            </div>
            <div className="modal-body">
            {submitReq.requestType === 'Purchase' && (
              <>
                <label className="detail-field"><span className="field-label">Date in Liquidation Status</span><input type="date" value={String(submitDetails.dateToSbaLiquidation ?? '')} onChange={e => setSubmitDetail('dateToSbaLiquidation', e.target.value)} /></label>
                <label className="detail-field"><span className="field-label">Date Purchase Submitted</span><input type="date" value={String(submitDetails.datePurchaseSubmitted ?? '')} onChange={e => setSubmitDetail('datePurchaseSubmitted', e.target.value)} /></label>
              </>
            )}
            {submitReq.requestType === 'OIC' && (
              <label className="detail-field"><span className="field-label">Date OIC Submitted</span><input type="date" value={String(submitDetails.oicSubmittedDate ?? '')} onChange={e => setSubmitDetail('oicSubmittedDate', e.target.value)} /></label>
            )}
            {submitReq.requestType === 'Lit Plan' && (
              <label className="detail-field"><span className="field-label">Date Lit Plan Submitted</span><input type="date" value={String(submitDetails.litPlanSubmittedDate ?? '')} onChange={e => setSubmitDetail('litPlanSubmittedDate', e.target.value)} /></label>
            )}
            {submitReq.requestType === 'Extension' && (
              <label className="detail-field"><span className="field-label">Date Extension Submitted</span><input type="date" value={String(submitDetails.dateExtensionRequested ?? '')} onChange={e => setSubmitDetail('dateExtensionRequested', e.target.value)} /></label>
            )}
            {submitReq.requestType === 'Charge Off' && (
              <>
                <label className="detail-field"><span className="field-label">Date Charge Off Submitted</span><input type="date" value={String(submitDetails.dateChargeOffSubmitted ?? '')} onChange={e => setSubmitDetail('dateChargeOffSubmitted', e.target.value)} /></label>
                {/* CPC rides on the charge-off rather than needing a package of its own
                    (#51). The analyst says up front whether one applies, the same way a
                    UPP's shape is chosen, so the form asks for the CPC figures rather
                    than leaving fields to be ignored. */}
                <label className="detail-field">
                  <span className="field-label">Charge Off Type</span>
                  <select value={String(submitDetails.chargeOffStatus ?? '')}
                    onChange={e => setSubmitDetail('chargeOffStatus', e.target.value)}>
                    <option value="">— select —</option>
                    {CHARGE_OFF_SHAPES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                {String(submitDetails.chargeOffStatus ?? '').includes('CPC') && (
                  <>
                    <label className="detail-field">
                      <span className="field-label">Date CPC Submitted</span>
                      <input type="date" value={String(submitDetails.cpcSubmittedDate ?? '')}
                        onChange={e => setSubmitDetail('cpcSubmittedDate', e.target.value)} />
                    </label>
                    <label className="detail-field">
                      <span className="field-label">Amount of CPC Submitted</span>
                      <input type="number" step="0.01" placeholder="0.00" value={String(submitDetails.cpcSubmittedAmount ?? '')}
                        onChange={e => setSubmitDetail('cpcSubmittedAmount', e.target.value)} />
                    </label>
                  </>
                )}
                {/* What was claimed from SBA, captured with the submission rather than at
                    decision — the analyst knows it when they send the package. */}
                <label className="detail-field">
                  <span className="field-label">Expenses Submitted</span>
                  <input type="number" step="0.01" placeholder="0.00" value={String(submitDetails.chargeOffExpensesSubmitted ?? '')}
                    onChange={e => setSubmitDetail('chargeOffExpensesSubmitted', e.target.value)} />
                </label>
              </>
            )}
            {submitReq.requestType === 'Return to Regular Servicing' && (
              <label className="detail-field"><span className="field-label">Date Submitted</span><input type="date" value={String(submitDetails.rtsSubmittedDate ?? '')} onChange={e => setSubmitDetail('rtsSubmittedDate', e.target.value)} /></label>
            )}
            <div className="modal-section-label">Status Update</div>
            <label className="detail-field">
              <span className="field-label">eTran Loan Status</span>
              <select value={submitEtranStatus} onChange={e => setSubmitEtranStatus(e.target.value)}>
                <option value="">— none —</option>
                {ETRAN_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="detail-field">
              <span className="field-label">Loan Status</span>
              <select value={submitSbaStatus} onChange={e => setSubmitSbaStatus(e.target.value)}>
                <option value="">— none —</option>
                {SBA_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            </div>

            <div className="modal-actions">
              <button className="btn-sm" onClick={() => { setSubmitReq(null); setSubmitDetails({}); }} disabled={submitting}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmitToSba} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit to SBA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          "Edit Package" modal (edits a package's fields directly)
          =================================================================== */}
      {editPkgReq && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Package</h2>
              <p className="modal-meta">{loan.borrowerName} · <strong>{editPkgReq.requestType}</strong></p>
            </div>
            <div className="modal-body">
              {editPkgReq.requestType && PACKAGE_FIELD_KEYS[editPkgReq.requestType]
                .filter(k => isFieldVisible(k, editPkgFields))
                .map(k => (
                <PackageFieldInput
                  key={k}
                  fieldKey={k}
                  value={editPkgFields[k]}
                  onChange={v => setEditPkgFields(f => ({ ...f, [k]: v }))}
                />
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn-sm" onClick={() => setEditPkgReq(null)} disabled={editPkgSaving}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveEditPkg} disabled={editPkgSaving}>
                {editPkgSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
